import { EditOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Col, Divider, message, Row, Spin,
  Switch,
  Typography
} from 'antd';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import getDropdownTypeService from 'common/services/dropdownType';
import { getAddOnPackageDetailService, updateAddOnPackageStatusService } from 'common/services/packages';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';
import { packageDetailSchema } from 'common/utils/schemas';
import { packageType } from 'extends/AddOnPackageDetail';
import PackageTierConfig from 'extends/AddOnPackageDetail/components/PackageTierConfig';
import { packageDurations } from 'extends/PackageDetail';

export type FormTierValues = {
  name: string;
  general: { numberOfAgent: number; };
  knowledgeLimits: {
    numberOfFile: number;
    totalSizeLimit: number;
    numberOfWebsite: number;
  };
  endUserSides: {
    numberOfSession: number;
  },
  price: number;
};

export type FormValues = {
  name: string;
  duration: { value: number; label: string };
  isApplyPayg: boolean;
  tiers: FormTierValues;
  type: { value: number; label: string };
  descriptions: string;
};
const AddOnPackageDetailView = () => {
  /* Hook */
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
    },
    resolver: yupResolver(packageDetailSchema),
  });
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  /* Search Params */
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));

  /* States */

  /* React-query */
  const { data: packageData, isLoading: isPackageDataLoading } = useQuery(
    ['getAddOnPackageDetail'],
    () => getAddOnPackageDetailService(idParams),
    {
      onSuccess: (data) => {
        method.reset({
          name: data.servicePackageAddOnData.name,
          duration: {
            label: packageDurations(t).find(
              (item) => item.value === data.servicePackageAddOnData.duration
            )?.label || '',
            value: data.servicePackageAddOnData.duration
          },
          descriptions: data.servicePackageAddOnData.description,
          type: {
            value: packageType().find(
              (item) => item.label.toLowerCase()
                === data.servicePackageAddOnData.addOnType.toLowerCase()
            )?.value || 0,
            label: packageType().find(
              (item) => item.label.toLowerCase().split(' ').join('')
                === data.servicePackageAddOnData.addOnType.toLowerCase()
            )?.label || '',
          },
          tiers: {
            general: {
              numberOfAgent: data.servicePackageAddOnData.numberOfAgent,
            },
            knowledgeLimits: {
              numberOfFile: data.servicePackageAddOnData.numberOfFile,
              totalSizeLimit: data.servicePackageAddOnData.totalSizeLimit,
              numberOfWebsite: data.servicePackageAddOnData.numberOfWebsite,
            },
            endUserSides: {
              numberOfSession: data.servicePackageAddOnData.numberOfSession,
            },
            price: data.servicePackageAddOnData.price,
          },
        });
      },
      enabled: !!searchParams.get('id'),
    }
  );

  const { mutate: updateStatusMutate } = useMutation(
    'updateFaqStatus',
    async (data: {
      id: number, statusId: number
    }) => updateAddOnPackageStatusService(data.id, data.statusId),
    {
      onSuccess: () => {
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(['getAddOnPackageDetail']);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      }
    }
  );

  const { data: customServices, isLoading: isCustomServicesLoading } = useQuery(
    ['getAllCustomServices'],
    () => getDropdownTypeService<any>('ai-assistant-service', {
      page: 1,
      limit: 200,
    }),
  );

  /* Functions */

  if (isPackageDataLoading || isCustomServicesLoading) {
    return <Spin />;
  }

  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.packageDetail')}
        rightHeader={(
          <Button
            type="primary"
            loading={isPackageDataLoading || isCustomServicesLoading}
            onClick={() => { navigate(`${ROUTE_PATHS.ADD_ON_PACKAGE_DETAIL}?id=${idParams}`); }}
          >
            <EditOutlined />
            {t('media.edit')}
          </Button>
        )}
      />
      {packageData && (
        <div className="t-mainlayout_wrapper">
          <div className="u-mt-16 p-packageDetail_actions">
            <Typography.Title level={2}>{t('package.generalInformation')}</Typography.Title>
            <Row>
              <Col span={12}>
                <table className="t-package_detail-right">
                  <tr>
                    <th>
                      {t('package.nameServicePackage')}
                      :
                    </th>
                    <td>
                      {packageData?.servicePackageAddOnData.name}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      {t('package.duration')}
                      :
                    </th>
                    <td>
                      {packageDurations(t)?.find((item) => item.value === packageData?.servicePackageAddOnData.duration)?.label || ''}
                    </td>
                  </tr>
                </table>
              </Col>
              <Col span={12}>
                <table className="t-package_detail-right">
                  <tr>
                    <th>
                      {t('package.createdBy')}
                      :
                    </th>
                    <td>
                      {packageData?.creator.name}
                      {' '}
                      -
                      {' '}
                      {formatDateTime(packageData?.servicePackageAddOnData.createdAt)}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      {t('package.lastModifiedBy')}
                      :
                    </th>
                    <td>
                      {packageData?.updater.name}
                      {' '}
                      -
                      {' '}
                      {formatDateTime(packageData?.servicePackageAddOnData.updatedAt)}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      {t('system.status')}
                      :
                    </th>
                    <td>
                      <Switch
                        checked={packageData.servicePackageAddOnData.status === 2}
                        onChange={(checked) => updateStatusMutate({
                          id: packageData.servicePackageAddOnData.id,
                          statusId: checked ? 2 : 1,
                        })}
                      />
                    </td>
                  </tr>
                </table>
              </Col>
            </Row>

            <Divider />
            <Typography.Title level={2}>{t('package.tierConfig')}</Typography.Title>
            <PackageTierConfig
              method={method}
              customServicesData={customServices?.data}
              isDisplay
            />
          </div>
        </div>
      )}
    </>
  );
};
export default AddOnPackageDetailView;
